// Load the browser environment eagerly. Pixi normally detects it and dynamic-imports it
// during Application.init, but that indirection is fragile under production tree-shaking
// — the side-effectful registration gets dropped and init then never resolves. Importing
// the entry point directly is both more robust and one less round trip at startup.
import 'pixi.js/browser';
import { Application, Container } from 'pixi.js';
import { VISUALS, WORLD_HEIGHT, WORLD_WIDTH } from '../config.ts';
import type { Vec2 } from '../math/vec2.ts';
import { PostProcess, type PostQuality } from './PostProcess.ts';
import { detectFloatTargets, type FloatTargetSupport } from './gpuFormats.ts';

/** The slice of world space currently visible, including any letterboxed overscan. */
export interface ViewRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * Owns the Pixi application and the world-to-screen mapping.
 *
 * The world is a fixed WORLD_WIDTH x WORLD_HEIGHT box. It is scaled to fit the viewport
 * with its aspect ratio preserved and centred; the background shader then paints the
 * whole screen, including the overscan, so a tall phone does not get black bars.
 *
 * Nothing outside this file may look at the canvas size — everything else works in world
 * units.
 */
export class Renderer {
  readonly app = new Application();

  /** World-space container: children use world units directly. */
  readonly world = new Container({ label: 'world' });

  readonly layers = {
    /** Full-screen table surface, behind everything. */
    background: new Container({ label: 'background' }),
    level: new Container({ label: 'level' }),
    fluid: new Container({ label: 'fluid' }),
    ball: new Container({ label: 'ball' }),
    magnets: new Container({ label: 'magnets' }),
    /** Transient rings and bursts, above everything on the board. */
    effects: new Container({ label: 'effects' }),
  };

  /** World units to CSS pixels. */
  scale = 1;
  offsetX = 0;
  offsetY = 0;

  /** Viewport size in CSS pixels. */
  viewWidth = 1;
  viewHeight = 1;

  readonly visible: ViewRect = { x0: 0, y0: 0, x1: WORLD_WIDTH, y1: WORLD_HEIGHT };

  private resizeListeners: Array<(view: ViewRect) => void> = [];

  /** Draw calls issued since the last resetDrawCalls(). Only counted in debug mode. */
  drawCalls = 0;

  readonly post = new PostProcess();

  /** What the GPU will render into. Valid after init(); conservative before it. */
  floatTargets: FloatTargetSupport = { halfFloat: false };

  /**
   * Camera shake, in world units. Applied to the world container rather than in the post
   * chain so the DOM overlay stays put — a HUD that shakes with the board reads as a bug.
   */
  private shakeX = 0;
  private shakeY = 0;

  async init(canvas: HTMLCanvasElement, quality: PostQuality = 'high'): Promise<void> {
    // Before app.init, because the antialias flag below depends on it and a WebGL context
    // cannot change its multisampling after creation.
    this.post.quality = quality;

    const resolution = Math.min(window.devicePixelRatio || 1, VISUALS.maxDevicePixelRatio);

    await this.app.init({
      canvas,
      width: canvas.clientWidth || window.innerWidth,
      height: canvas.clientHeight || window.innerHeight,
      resolution,
      autoDensity: true,
      // With the post chain on, the only thing that ever reaches the root framebuffer is
      // the composite quad — multisampling it would cost memory and bandwidth to
      // antialias two triangles that cover the screen. The scene is rendered into
      // sceneTexture, which does its own (non-multisampled) thing.
      antialias: !this.post.enabled,
      backgroundColor: 0x05060d,
      powerPreference: 'high-performance',
      preference: 'webgl',
    });

    // Half-float scene and bloom buffers if the driver allows them. Must be settled
    // before the resize below, which is what allocates the chain.
    this.floatTargets = detectFloatTargets(this.app.renderer);
    this.post.setHdr(this.floatTargets.halfFloat);

    // We drive rendering ourselves so simulation and render cost can be timed separately
    // and so the fluid's density pass always runs before the frame that samples it.
    this.app.ticker.remove(this.app.render, this.app);

    this.world.addChild(
      this.layers.background,
      this.layers.level,
      this.layers.fluid,
      this.layers.ball,
      this.layers.magnets,
      this.layers.effects,
    );
    this.app.stage.addChild(this.world);

    this.attachContextRecovery(canvas);
    this.resize(canvas.clientWidth || window.innerWidth, canvas.clientHeight || window.innerHeight);
  }

  /**
   * Mobile GPUs drop the WebGL context on memory pressure, backgrounding or a tab switch.
   * Calling preventDefault on the loss event is what tells the browser to hand the context
   * back rather than leaving a dead canvas behind.
   */
  private attachContextRecovery(canvas: HTMLCanvasElement): void {
    canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      console.warn('[magneto] WebGL context lost; waiting for restore');
    });
    canvas.addEventListener('webglcontextrestored', () => {
      console.warn('[magneto] WebGL context restored');
      this.resizeListeners.forEach((listener) => listener(this.visible));
    });
  }

  /**
   * Count real draw calls by wrapping the GL context's draw entry points.
   *
   * Pixi has no counter of its own, and "how many draws is this costing" is exactly the
   * question you want answered when a phone drops frames. Only enabled behind ?debug=1 —
   * the wrappers are cheap but not free.
   */
  enableDrawCallCounting(): void {
    const gl = (this.app.renderer as unknown as { gl?: WebGL2RenderingContext }).gl;
    if (!gl) return;

    const methods = [
      'drawArrays',
      'drawElements',
      'drawArraysInstanced',
      'drawElementsInstanced',
    ] as const;

    for (const name of methods) {
      const original = gl[name];
      if (typeof original !== 'function') continue;
      (gl as unknown as Record<string, unknown>)[name] = (...args: unknown[]) => {
        this.drawCalls++;
        return (original as (...a: unknown[]) => unknown).apply(gl, args);
      };
    }
  }

  resetDrawCalls(): void {
    this.drawCalls = 0;
  }

  onResize(listener: (view: ViewRect) => void): void {
    this.resizeListeners.push(listener);
    listener(this.visible);
  }

  resize(width: number, height: number): void {
    const nextWidth = Math.max(1, width);
    const nextHeight = Math.max(1, height);
    const resolution = Math.min(window.devicePixelRatio || 1, VISUALS.maxDevicePixelRatio);

    // iOS fires visualViewport resize continuously while the URL bar slides; reallocating
    // the drawing buffer on every one of those is how a smooth scroll turns into a stall.
    if (
      nextWidth === this.viewWidth &&
      nextHeight === this.viewHeight &&
      resolution === this.app.renderer.resolution
    ) {
      return;
    }

    this.viewWidth = nextWidth;
    this.viewHeight = nextHeight;
    this.app.renderer.resolution = resolution;
    this.app.renderer.resize(this.viewWidth, this.viewHeight);

    // Contain, not cover: the whole playfield must always be on screen.
    this.scale = Math.min(this.viewWidth / WORLD_WIDTH, this.viewHeight / WORLD_HEIGHT);
    this.offsetX = (this.viewWidth - WORLD_WIDTH * this.scale) * 0.5;
    this.offsetY = (this.viewHeight - WORLD_HEIGHT * this.scale) * 0.5;

    this.world.position.set(this.offsetX, this.offsetY);
    this.world.scale.set(this.scale);

    this.visible.x0 = -this.offsetX / this.scale;
    this.visible.y0 = -this.offsetY / this.scale;
    this.visible.x1 = this.visible.x0 + this.viewWidth / this.scale;
    this.visible.y1 = this.visible.y0 + this.viewHeight / this.scale;

    if (this.post.enabled) {
      this.post.resize(this.viewWidth, this.viewHeight, resolution);
    }

    for (const listener of this.resizeListeners) listener(this.visible);
  }

  /** Convert a pointer position (CSS pixels, relative to the canvas) to world units. */
  screenToWorld(screenX: number, screenY: number, out: Vec2): Vec2 {
    out.x = (screenX - this.offsetX) / this.scale;
    out.y = (screenY - this.offsetY) / this.scale;
    return out;
  }

  /** Convert a world-space length to CSS pixels — used for gesture thresholds. */
  screenLengthToWorld(pixels: number): number {
    return pixels / this.scale;
  }

  /** Offset the board this frame. Set by the effects system, cleared after rendering. */
  setShake(x: number, y: number): void {
    this.shakeX = x;
    this.shakeY = y;
  }

  setPostQuality(quality: PostQuality): void {
    this.post.quality = quality;
    if (quality !== 'off') {
      this.post.resize(this.viewWidth, this.viewHeight, this.app.renderer.resolution);
    }
  }

  render(): void {
    this.world.position.set(
      this.offsetX + this.shakeX * this.scale,
      this.offsetY + this.shakeY * this.scale,
    );

    const target = this.post.sceneTarget;
    if (target) {
      this.app.renderer.render({ container: this.app.stage, target, clear: true });
      this.post.present(this.app.renderer);
    } else {
      this.app.renderer.render(this.app.stage);
    }
  }

  destroy(): void {
    this.post.destroy();
    this.resizeListeners = [];
    this.app.destroy(true, { children: true });
  }
}
